import { useEffect, useState, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import api, { fileUrl } from "@/lib/api";
import { IMAGES } from "@/data/site";
import { SectionHeader } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Section } from "@/components/Section";
import { Play, MapPin, ImageIcon, Loader2 } from "lucide-react";

const CURATED = [
  { title: "Mega Solar Photovoltaic Park — Drone SCADA Inspection", category: "solar", location: "Neemuch, MP", img: IMAGES.solarHero, media_type: "photo" },
  { title: "Central India 132kV Grid Substation", category: "solar", location: "Bhopal District, MP", img: IMAGES.substation, media_type: "photo" },
  { title: "Eco-Friendly Aggregate Quarry with Mist Suppression", category: "mining", location: "Katni, MP", img: IMAGES.miningPit, media_type: "photo" },
  { title: "Curved LED Virtual Production Stage — OTT Series", category: "media", location: "Bhopal Studio, MP", img: IMAGES.mediaHero, media_type: "video" },
  { title: "Commercial Single-Axis Tracker Solar Field", category: "solar", location: "Indore Zone, MP", img: IMAGES.solarField, media_type: "photo" },
  { title: "Hydraulic Excavation & Primary Crushing Unit", category: "mining", location: "Katni Cluster, MP", img: IMAGES.miningTruck, media_type: "photo" },
  { title: "Behind the Scenes: LED Wall Broadcast", category: "media", location: "Bhopal Studio, MP", img: IMAGES.mediaStudio, media_type: "video" },
  { title: "220kV Central Pooling Substation Transformer", category: "solar", location: "Rewa, MP", img: IMAGES.transformer, media_type: "photo" },
];

function Card({ item }) {
  const src = item.storage_path ? fileUrl(item.storage_path) : item.img;
  const isVideo = item.media_type === "video" || item.content_type?.startsWith("video");
  return (
    <div className="group relative oj-card overflow-hidden" data-testid="gallery-item">
      <div className="relative h-56 overflow-hidden bg-teal-950">
        {isVideo && item.storage_path ? (
          <video src={src} className="w-full h-full object-cover" muted loop playsInline onMouseOver={(e) => e.target.play()} onMouseOut={(e) => e.target.pause()} />
        ) : (
          <img src={src} alt={item.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#014845] via-transparent to-transparent opacity-85" />
        {isVideo && (
          <div className="absolute inset-0 grid place-items-center pointer-events-none">
            <div className="grid place-items-center w-14 h-14 rounded-full bg-teal-600/80 border border-white/30 backdrop-blur group-hover:scale-110 transition-transform"><Play className="w-6 h-6 text-white ml-0.5" fill="white" /></div>
          </div>
        )}
        <span className="absolute top-3 left-3 px-2.5 py-1 rounded-md text-[10px] font-mono uppercase tracking-wider bg-teal-950/85 text-teal-200 border border-teal-400/30">{item.category}</span>
      </div>
      <div className="p-5">
        <h4 className="font-semibold text-white text-[15px] leading-snug line-clamp-2">{item.title}</h4>
        {item.location && <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-600"><MapPin className="w-3.5 h-3.5 text-teal-400" /> {item.location}</div>}
      </div>
    </div>
  );
}

export default function Gallery() {
  const { t } = useApp();
  const g = t.galleryP;
  const CATS = g.cats;
  const [cat, setCat] = useState("all");
  const [uploaded, setUploaded] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    api.get("/media").then(({ data }) => setUploaded(data)).catch((e) => console.error(e)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const combined = [...uploaded, ...CURATED];
  const filtered = cat === "all" ? combined : combined.filter((i) => i.category === cat);

  return (
    <div>
      <Section sid="gallery.head" name="Gallery Header" className="border-b border-teal-900/40 bg-[#E9F5F4]/40 py-14">
        <div className="oj-container">
          <div className="oj-eyebrow mb-3"><ImageIcon className="w-4 h-4" /> {g.eyebrow}</div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0B3B39] tracking-tight">{g.title}</h1>
          <p className="mt-4 text-slate-600 max-w-3xl leading-relaxed">{g.sub}</p>
        </div>
      </Section>

      <Section sid="gallery.grid" name="Gallery Grid" className="oj-container py-12">
        <div className="flex flex-wrap gap-3 mb-10">
          {CATS.map(([id, label]) => (
            <button key={id} onClick={() => setCat(id)} data-testid={`gallery-filter-${id}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${cat === id ? "bg-[#017471] text-white shadow-lg shadow-teal-900/20" : "bg-white text-slate-600 hover:bg-teal-50 border border-teal-200"}`}>
              {label}
            </button>
          ))}
        </div>
        {loading ? (
          <div className="grid place-items-center py-20 text-teal-400"><Loader2 className="w-8 h-8 animate-spin" /></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filtered.map((item, i) => (
              <Reveal key={item.id || i} delay={(i % 4) * 0.06}><Card item={item} /></Reveal>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
